import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
    useCreateSectorMutation,
    useUpdateSectorMutation,
} from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import CommonDialog from "../../../components/common/CommonDialog";

const SectorDialog = ({ open, onClose, rowData, isEdit, title, ctaText }) => {
    const { control, handleSubmit, setValue, reset, setError } = useForm();
    const [createSector, { isLoading: isCreating }] = useCreateSectorMutation();
    const [updateSector, { isLoading: isUpdating }] = useUpdateSectorMutation();
    const { showNotification } = useNotification();

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                setValue(key, rowData[key]);
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(data).forEach((key) => {
                formDataToSend.append(key, data[key]);
            });
            if (isEdit) {
                await updateSector({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Sector Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Sector"
                            );
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            } else {
                await createSector({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Sector Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Sector"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update sector:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <Controller
                name="name"
                control={control}
                rules={{
                    required: "Name is required",
                    pattern: {
                        value: /^[a-zA-Z0-9\s\-\&\(\)\;\\/]+$/,
                        message: "Name can only contain letters, numbers, spaces, hyphens, ampersands, semicolons, forward slashes and parentheses"
                    }
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField label="Name" {...field} required />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <ActionButtons
                onCancel={handleClose}
                onSave={handleSubmit(onSubmit)}
                isLoading={isCreating || isUpdating}
                ctaText={ctaText}
            />
        </CommonDialog>
    );
};

SectorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    isEdit: PropTypes.bool,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default SectorDialog;
